import React, { useState, useEffect, lazy, Suspense, useMemo } from "react";
import {
  ButtonType,
  ButtonShape,
  ButtonSize,
} from "../../../../enums/ButtonAttributesEnums";
import TNButton from "../../../../reusable-components/TNButton";
import styles from "./SalesPersonCustomerApproval.module.scss";
import { Layout, Menu, Typography, Modal, Input, message, Tag } from "antd";
import CreateJobServices from "../../../../services/createJob.services";
import { UploadDocType } from "../../CreateJobView/JobLead/constants";
import acceptIcon from "../../../../assets/images/accept-icon.svg";
import rejectIcon from "../../../../assets/images/reject-icon.svg";
import {
  ApprovalStatus,
  defaultApprovalStates,
  getSectionStatus,
  ImageAltText,
  SalesPersonViewConstants,
  sectionStatusMapTemplate,
  sectionTemplate,
} from "./constants";
import clsx from "clsx";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import { jobStatus } from "../../CreateJobView/constants";
import { SectionStatus } from "../../../../enums/SubSectionStatus";
import { DocTypes } from "../../../../enums/DocTypes";
import fileIcon from "../../../../assets/images/default_file.svg";
import { FileTypes } from "../../../../enums/FileUploadEnums";
import { iconMapping, ViewJobConstants } from "../constants";
import TNLoader from "../../../../reusable-components/TNLoader";
const LazyDocumentViewer = lazy(
  () => import("../../../../reusable-components/MultiFileViewer"),
);

const SalesPersonCustomerApproval = ({
  jobDetails,
  setInternalApprovalPayload,
  userId,
}) => {
  const { Title } = Typography;
  const { Sider, Content } = Layout;
  const { TextArea } = Input;
  const { pdfAltText } = ImageAltText;
  const { CASH_OR_CREDIT_DOCS_TITLE, PROJECT_INFO_SHEET_TITLE } =
    ViewJobConstants;
  const { FetchAllDocuments } = CreateJobServices();
  const { isFetching, data } = FetchAllDocuments(jobDetails?.id);
  const projectInformationSheet = data?.filter(
    (doc) => doc.docType === UploadDocType.projectDoc
  );
  const cashOrCreditDocuments = data?.filter(
    (doc) => doc.docType === DocTypes.CASH_CREDIT_SALE_DOCUMENTS
  );
  const bondInfoDocument = data?.find(
    (doc) => doc?.docType === DocTypes.BOND_INFO_DOCUMENT,
  );

  const cashOrCreditSections = useMemo(() => cashOrCreditDocuments?.length ? [
    {
      key: "1",
      title: CASH_OR_CREDIT_DOCS_TITLE,
      pdfs: cashOrCreditDocuments,
    },
  ] : [], [cashOrCreditDocuments]);

  const pdfSections = useMemo(() => [
    {
      key: "1",
      title: PROJECT_INFO_SHEET_TITLE,
      pdfs: projectInformationSheet,
    },
  ], [projectInformationSheet]);

  const subSections = sectionTemplate.map((subSection) => {
    const matchingPdfs = data?.filter(
      (doc) => doc?.docType === subSection?.title,
    );

    const sectionStatus =
      jobDetails?.approvalDocuments?.[subSection.title]?.status;
    const review = jobDetails?.approvalDocuments?.[subSection.title]?.review;
    const isSameUser = review?.some((rev) => rev?.reviewedBy === userId);
    const jobStageStatus = jobDetails?.stageId;

    const status = getSectionStatus(jobStageStatus, sectionStatus, isSameUser);

    return {
      ...subSection,
      pdfs: matchingPdfs,
      status,
    };
  });

  const [sectionStatusMap, setSectionStatusMap] = useState(sectionStatusMapTemplate);

  const updateSectionStatus = (section, status, reason = "") => {
    setSectionStatusMap((prev) => ({
      ...prev,
      [section]: {
        status,
        reasonForRejection: status === ApprovalStatus.rejected ? reason : "",
      },
    }));
  };

  const [isRejectReasonModalOpen, setIsRejectReasonModalOpen] = useState(false);
  const [bufferText, setBufferText] = useState(null);
  const [projectInfoSectionRejectReason, setProjectInfoSectionRejectReason] =
    useState(null);
  const [
    propertyLegalDescriptionRejectReason,
    setPropertyLegalDescriptionRejectReason,
  ] = useState(null);
  const [
    propertyOwnerInformationRejectReason,
    setPropertyOwnerInformationRejectReason,
  ] = useState(null);
  const [
    generalContractorInformationRejectReason,
    setGeneralContractorInformationRejectReason,
  ] = useState(null);
  const [
    subContractorInformationRejectReason,
    setSubContractorInformationRejectReason,
  ] = useState(null);
  const [bondInformationRejectReason, setBondInformationRejectReason] =
    useState(null);

  const rejectReason = {
    "Project Info Section": projectInfoSectionRejectReason,
    "Property Legal Description": propertyLegalDescriptionRejectReason,
    "Property Owner Information": propertyOwnerInformationRejectReason,
    "General Contractor Information": generalContractorInformationRejectReason,
    "Subcontractor Information": subContractorInformationRejectReason,
    "Bond Information": bondInformationRejectReason,
  };

  const setRejectReason = {
    "Project Info Section": setProjectInfoSectionRejectReason,
    "Property Legal Description": setPropertyLegalDescriptionRejectReason,
    "Property Owner Information": setPropertyOwnerInformationRejectReason,
    "General Contractor Information":
      setGeneralContractorInformationRejectReason,
    "Subcontractor Information": setSubContractorInformationRejectReason,
    "Bond Information": setBondInformationRejectReason,
  };

  const [currentSection, setCurrentSection] = useState(null);

  const handleRejectReasonModalClose = () => {
    setRejectReason?.[currentSection]?.(bufferText);
    setBufferText(null);
    setIsRejectReasonModalOpen(false);
  };
  const handleRejectReasonModalSubmit = () => {
    if (
      rejectReason?.[currentSection] === "" ||
      rejectReason?.[currentSection] === null
    ) {
      message.error("Enter reject reason to submit");
      setIsRejectReasonModalOpen(true);
      return;
    }
    updateSectionStatus(
      currentSection,
      ApprovalStatus.rejected,
      rejectReason?.[currentSection]
    );
    setIsRejectReasonModalOpen(false);
  };

  const handleSectionApprove = (sec?: string) => {
    updateSectionStatus(sec, ApprovalStatus.approved);
  };
  const handleSectionReject = (sec?: string) => {
    setIsRejectReasonModalOpen(true);
    setCurrentSection(sec);
    setBufferText(rejectReason?.[sec]);
  };

  const [selectedPdf, setSelectedPdf] = useState(null);
  const [selectedMenuKey, setSelectedMenuKey] = useState(null);
  const [pdfHeader, setPdfHeader] = useState("Overall Project Info Sheet"); // Set default header

  const handleSetSelectedPdf = (pdf) => {
    setSelectedPdf(pdf);
  };

  const memoizedDocs = useMemo(() => {
    if (!selectedPdf) return [];
    return [
      {
        uri: selectedPdf?.docAttachment,
        fileName: selectedPdf?.name,
      },
    ];
  }, [selectedPdf?.docAttachment, selectedPdf?.name]);

  const downloadDocument = () => {
    if (!selectedPdf) return;

    const { docAttachment, name } = selectedPdf;
    fetch(docAttachment)
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(console.error);
  };

  const handleSectionsClick = (title, pdf) => {
    setPdfHeader(title);
    setSelectedMenuKey([pdf?.id]);
    handleSetSelectedPdf(pdf);
  };

  useEffect(() => {
    setInternalApprovalPayload(sectionStatusMap);
  }, [sectionStatusMap]);

  useEffect(() => {
    const updatedStatusMap = { ...sectionStatusMap };

    Object.keys(updatedStatusMap).forEach((sectionTitle) => {
      const sectionStatus =
        jobDetails?.approvalDocuments?.[sectionTitle]?.status;
      const review = jobDetails?.approvalDocuments?.[sectionTitle]?.review;
      const isSameUser = review?.some((rev) => rev?.reviewedBy === userId);
      const jobStageStatus = jobDetails?.stageId;

      updatedStatusMap[sectionTitle].status = getSectionStatus(
        jobStageStatus,
        sectionStatus,
        isSameUser,
      );
    });

    setSectionStatusMap(updatedStatusMap);
  }, [jobDetails?.approvalDocuments, userId]);

  // Set default PDF when component mounts or when data loads
  useEffect(() => {
    if (!!jobDetails?.cashOrCreditSale) {

      if(cashOrCreditDocuments?.[0]) {
        return handleSectionsClick(
          cashOrCreditSections[0].title,
          cashOrCreditDocuments[0],
        );
      }
      
      if(bondInfoDocument)
        return handleSectionsClick(
            DocTypes.BOND_INFO_DOCUMENT, 
            bondInfoDocument
          );

      return handleSectionsClick(SalesPersonViewConstants.NO_DOCUMENTS_TITLE, null);
    }
    if (projectInformationSheet?.[0]) {
      handleSectionsClick(pdfSections[0].title, projectInformationSheet[0]);
    } else if (subSections?.[0]?.pdfs?.[0]) {
      handleSectionsClick(subSections[0].title, subSections[0].pdfs[0]);
    }
  }, [data]); // Add data as dependency to re-run when documents load

  return (
    <>
      <TNLoader isLoading={isFetching} />
      <Modal
        width={750}
        visible={isRejectReasonModalOpen}
        onCancel={handleRejectReasonModalClose}
        title="Reject Reason"
        okText="Submit"
        onOk={handleRejectReasonModalSubmit}
        okButtonProps={{
          disabled: !rejectReason,
        }}
      >
        <TextArea
          rows={5}
          showCount
          maxLength={500}
          value={rejectReason?.[currentSection]}
          onChange={(e) => setRejectReason?.[currentSection]?.(e.target.value)}
          placeholder="Enter reject reason"
        />
      </Modal>
      <Layout style={{ height: "100vh" }}>
        <Content className={styles.PdfContainer}>
          <div className={styles.PdfContainerHeader}>
            <div>
              <h3>{pdfHeader}</h3>
            </div>
            <div>
              <TNButton
                type={ButtonType.GHOST}
                shape={ButtonShape.DEFAULT}
                size={ButtonSize.LARGE}
                text="Download"
                onClick={downloadDocument}
                disabled={!selectedPdf}
              />
            </div>
          </div>
          {selectedPdf ? (
            <div className={styles.viewerWrapper}>
              <Suspense>
                <LazyDocumentViewer docs={memoizedDocs} />
              </Suspense>
            </div>
          ) : (
            <ConditionalRender
              visible={
                !!bondInfoDocument
                || cashOrCreditDocuments?.length > 0
              }
            >
              <div className={styles.NoPdfSelected}>
                Select a document to view
              </div>
            </ConditionalRender>
          )}
        </Content>

        <Sider width="25%" theme="light" className={styles.SiderContainer}>
          <Menu
            mode="inline"
            defaultOpenKeys={pdfSections.map((section) => section.key)}
            selectedKeys={selectedMenuKey}
            className={styles.SectionMenuContainer}
          >
            <Menu.SubMenu
              key="project details"
              title="Project Details"
              className={styles.SectionSubMenu}
            >
              <Menu.Item
                style={{
                  paddingLeft: "18px",
                }}
              >
                <div>
                  <table>
                    <tr>
                      <td>Dropdown1</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.approvalInfo?.dropDown1}
                      </td>
                    </tr>
                    <tr>
                      <td>Dropdown2</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.approvalInfo?.dropDown2}
                      </td>
                    </tr>
                    <tr>
                      <td>Dropdown3</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.approvalInfo?.dropDown3}
                      </td>
                    </tr>
                  </table>
                </div>
              </Menu.Item>
              <Title level={5} className={styles.Title}>
                On-Site Contact
              </Title>
              <Menu.Item
                style={{
                  paddingLeft: "18px",
                }}
              >
                <div>
                  <table>
                    <tr>
                      <td>Name</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.approvalInfo?.name}
                      </td>
                    </tr>
                    <tr>
                      <td>Phone</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.approvalInfo?.phone}
                      </td>
                    </tr>
                    <tr>
                      <td>Email</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.approvalInfo?.email}
                      </td>
                    </tr>
                  </table>
                </div>
              </Menu.Item>
              <Title level={5} className={styles.Title}>
                Project Coordinates
              </Title>
              <Menu.Item
                style={{
                  paddingLeft: "18px",
                }}
              >
                <div>
                  <table>
                    <tr>
                      <td>Job Site</td>
                      <td className={styles.DropDown}>
                        {jobDetails?.dropOffLatLng?.lng}° N,{" "}
                        {jobDetails?.dropOffLatLng?.lat}° W
                      </td>
                    </tr>
                  </table>
                </div>
              </Menu.Item>
              <div className={styles.poInfoTitle}>
                <Title level={5} className={styles.Title}>
                  Invoice Notes
                </Title>
                <span>{"(Ex job & PO Number)"}</span>
              </div>
              <Menu.Item
                style={{
                  paddingLeft: "18px",
                }}
              >
                <div className={styles.poInfoContent}>
                  <table>
                    <tr>{jobDetails?.approvalInfo?.poInformation?.poInfo1}</tr>
                    <tr>{jobDetails?.approvalInfo?.poInformation?.poInfo2}</tr>
                    <tr>{jobDetails?.approvalInfo?.poInformation?.poInfo3}</tr>
                    <tr>{jobDetails?.approvalInfo?.poInformation?.poInfo4}</tr>
                  </table>
                </div>
              </Menu.Item>
              <Title level={5} className={styles.Title}>
                Project Bond Status
              </Title>
              <Menu.Item
                style={{
                  paddingLeft: "18px",
                }}
                key={bondInfoDocument?.id}
                onClick={() => {
                  if (!!bondInfoDocument)
                    handleSectionsClick(
                      DocTypes.BOND_INFO_DOCUMENT,
                      bondInfoDocument,
                    );
                }}
              >
                <ConditionalRender
                  visible={bondInfoDocument}
                  fallback={<p>No Project Bond Document</p>}
                >
                  <img
                    src={iconMapping[bondInfoDocument?.fileType] || fileIcon}
                    alt="PDF Icon"
                    className={styles.PdfIcon_SubSection}
                  />{" "}
                  {bondInfoDocument?.name ?? ""}
                </ConditionalRender>
              </Menu.Item>
            </Menu.SubMenu>
            {!!jobDetails?.cashOrCreditSale &&
              cashOrCreditSections?.map((section) => (
                  <Menu.SubMenu
                    key={section?.key}
                    title={section?.title}
                    className={styles.SectionSubMenu}
                  >
                    {section?.pdfs?.map((file) => (
                      <Menu.Item
                        key={file?.id}
                        onClick={() => {
                          handleSectionsClick(section.title, file);
                        }}
                        className={styles.SubSectionMenu}
                      >
                        <img
                          src={
                            iconMapping[file?.type] ??
                            (iconMapping[file?.fileType] || fileIcon)
                          }
                          alt={pdfAltText}
                          width={"25rem"}
                          className={styles.PdfIcon_SubSection}
                        />{" "}
                        {file?.name}
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                )
              )}
            {!!!jobDetails?.cashOrCreditSale &&
              pdfSections.map((section) => (
                <Menu.SubMenu
                  key={section?.key}
                  title={section?.title}
                  className={styles.SectionSubMenu}
                >
                  {section?.pdfs?.map((file) => (
                    <Menu.Item
                      key={file?.id}
                      onClick={() => {
                        handleSectionsClick(section.title, file);
                      }}
                      className={styles.SubSectionMenu}
                    >
                      <img
                        src={
                          iconMapping[file?.type] ??
                          (iconMapping[file?.fileType] || fileIcon)
                        }
                        alt={pdfAltText}
                        width={"25rem"}
                        className={styles.PdfIcon_SubSection}
                      />{" "}
                      {file?.name}
                    </Menu.Item>
                  ))}
                  {subSections?.map((sub) => (
                    <React.Fragment key={sub.key}>
                      <Title className={styles.Title}>
                        {sub?.title}
                        <div>
                          <ConditionalRender
                            visible={sub?.status === ApprovalStatus.pending}
                            fallback={
                              <Tag
                                className={clsx({
                                  [styles.CustomTag_Rejected]:
                                    sub?.status === ApprovalStatus.rejected,
                                  [styles.CustomTag_Approved]:
                                    sub?.status === ApprovalStatus.approved,
                                })}
                              >
                                {sub?.status}
                              </Tag>
                            }
                          >
                            <img
                              src={acceptIcon}
                              onClick={() => handleSectionApprove(sub?.title)}
                              className={clsx({
                                [styles.ApprovalIconsActive]:
                                  sectionStatusMap[sub?.title]?.status ===
                                  ApprovalStatus.approved,
                                [styles.ApprovalIconInactice]:
                                  sectionStatusMap[sub?.title]?.status ===
                                  ApprovalStatus.rejected,
                                [styles.ApprovalIconsDefault]:
                                  sectionStatusMap[sub?.title]?.status ===
                                  ApprovalStatus.pending,
                              })}
                            />
                            <img
                              src={rejectIcon}
                              onClick={() => handleSectionReject(sub?.title)}
                              className={clsx({
                                [styles.ApprovalIconsActive]:
                                  sectionStatusMap[sub?.title]?.status ===
                                  ApprovalStatus.rejected,
                                [styles.ApprovalIconInactice]:
                                  sectionStatusMap[sub?.title]?.status ===
                                  ApprovalStatus.approved,
                                [styles.ApprovalIconsDefault]:
                                  sectionStatusMap[sub?.title]?.status ===
                                  ApprovalStatus.pending,
                              })}
                            />
                          </ConditionalRender>
                        </div>
                      </Title>
                      {sub?.pdfs?.map((file) => (
                        <Menu.Item
                          key={file?.id}
                          onClick={() => {
                            handleSectionsClick(sub?.title, file);
                          }}
                          className={styles.SubSectionMenu}
                        >
                          <img
                            src={iconMapping[file?.fileType] || fileIcon}
                            alt={pdfAltText}
                            className={styles.PdfIcon_SubSection}
                          />{" "}
                          {file?.name}
                        </Menu.Item>
                      ))}
                    </React.Fragment>
                  ))}
                </Menu.SubMenu>
              ))}
          </Menu>
        </Sider>
      </Layout>
    </>
  );
};

export default SalesPersonCustomerApproval;
